import React from 'react';
import PropTypes from 'prop-types';

/**
 * Optimized Image component that serves WebP with JPEG fallback
 * and responsive sizes for different screen widths
 */
const OptimizedImage = ({
  src,
  alt,
  className = '',
  loading = 'lazy',
  sizes = '(max-width: 600px) 400px, (max-width: 1200px) 800px, 1200px',
  style = {},
  onClick = null
}) => {
  // Extract base name from src (remove extension if present)
  const baseName = src.replace(/\.(jpg|jpeg|png|webp)$/i, '');
  
  // Generate srcSet for WebP and JPEG formats
  const webpSrcSet = `
    ${baseName}-small.webp 400w,
    ${baseName}-medium.webp 800w, 
    ${baseName}-large.webp 1200w
  `.trim();
  
  const jpegSrcSet = `
    ${baseName}-small.jpeg 400w,
    ${baseName}-medium.jpeg 800w,
    ${baseName}-large.jpeg 1200w  
  `.trim();
  
  return (
    <picture>
      {/* WebP format for modern browsers */}
      <source 
        srcSet={webpSrcSet} 
        sizes={sizes} 
        type="image/webp" 
      />
      
      {/* JPEG fallback for older browsers */}
      <source 
        srcSet={jpegSrcSet} 
        sizes={sizes} 
        type="image/jpeg" 
      />
      
      {/* Fallback img element */}
      <img
        src={`${baseName}-medium.jpeg`}
        alt={alt}
        className={className}
        loading={loading}
        decoding="async"
        style={style}
        onClick={onClick}
        onError={(e) => {
          // Fallback to original image if optimized version fails
          console.warn(`Failed to load optimized image: ${e.target.src}`);
          // You could implement a further fallback here
        }}
      />
    </picture>
  );
};

OptimizedImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  loading: PropTypes.oneOf(['lazy', 'eager']),
  sizes: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.func
};

export default OptimizedImage;
